import {
  addDefaultTemplateTag,
  addTag,
  deleteTag,
  removeDefaultTemplateTag,
  updateTag,
} from "api/siteSettings/Tags";
import { isEqual, printError, useSearch, useUtilities } from "helpers";
import {
  addSingleTag,
  deleteSingleTag,
  updateSingleTag,
} from "redux/reducers/siteSettings/tagsReducer";
import { useMutation } from "@tanstack/react-query";
import { ApiResponseType, ModulePrefixes, NotificationType } from "enums";
import { t } from "i18next";
import { queryClient } from "client";

export function useDeleteTag(redirect = "") {
  const { dispatch, notification, navigate } = useUtilities();
  return useMutation(deleteTag, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(deleteSingleTag({ id: data.id }));
        notification({
          type: "success",
          message: t("siteSetting.tag.api.success.tagDeleted"),
        });
        if (redirect) navigate(redirect);
        queryClient.invalidateQueries(["tagList"]);
      } else {
        notification({
          type: "error",
          message: data.message,
        });
      }
    },
    onError: (err) => {
      notification({
        type: "error",
        message: t("common.error.somethingWentWrongTryLater"),
      });
    },
  });
}

export function useUpdateTag() {
  const { dispatch, notification } = useUtilities();
  return useMutation(updateTag, {
    onSuccess: (data) => {
      if (data.status_code === ApiResponseType.SUCCESS) {
        dispatch(updateSingleTag(data.data));
        notification({
          message: t("siteSetting.tag.api.success.tagUpdated"),
          type: "success",
        });
        queryClient.invalidateQueries(["tagList"]);
      } else {
        notification({
          message: printError(data.message),
          type: NotificationType.ERROR,
        });
      }
    },
    onError: (error) => {
      notification({
        message: printError(error?.response?.data?.message),
        type: "error",
      });
    },
  });
}

export function useAddTag(isSettings, navigateToDetails = true) {
  const { notification, dispatch, navigate } = useUtilities();
  const { web } = useSearch();
  return useMutation(addTag, {
    onSuccess: (data) => {
      if (!isEqual(data?.status_code, ApiResponseType.SUCCESS)) {
        notification({
          message: data?.message ?? t("common.error.somethingWentWrong"),
          type: "error",
        });
        return;
      }
      dispatch(addSingleTag(data.data));
      if (navigateToDetails) {
        navigate(
          !isSettings
            ? `/${web ? ModulePrefixes.SITE_SETTINGS : ModulePrefixes.SITE_TEMPLATE}/tags/${data.data.id}${web ? `?web=${web}` : ""}`
            : `/account-settings/tags/${data.data.id}`
        );
      }
      notification({
        message: t("siteSetting.tag.api.success.tagAdded"),
        type: "success",
      });
      queryClient.invalidateQueries(["tagList"]);
    },
    onError: (error) => {
      notification({
        message:
          error?.response?.data?.message ??
          t("common.error.somethingWentWrongTryLater"),
        type: "error",
      });
    },
  });
}

export function useAddDefaultTemplateTag() {
  const { notification } = useUtilities();
  return useMutation(addDefaultTemplateTag, {
    onSuccess: (data) => {
      if (data.status_code === ApiResponseType.SUCCESS) {
        queryClient.invalidateQueries(["tagList"]);
        notification({
          message: t("siteSetting.tag.api.success.tagAddedToSiteTemplate"),
          type: "success",
        });
      } else {
        notification({
          message: printError(data.message),
          type: NotificationType.ERROR,
        });
      }
    },
    onError: (error) => {
      notification({
        message: printError(
          error?.response?.data?.message ?? error?.response?.data?.message
        ),
        type: "error",
      });
    },
  });
}

export function useRemoveDefaultTemplateTag() {
  const { notification } = useUtilities();
  return useMutation(removeDefaultTemplateTag, {
    onSuccess: (data) => {
      if (data.status_code === ApiResponseType.SUCCESS) {
        queryClient.invalidateQueries(["tagList"]);
        notification({
          message: t("siteSetting.tag.api.success.tagRemovedFromSiteTemplate"),
          type: "success",
        });
      } else {
        return Promise.reject(data);
      }
    },
    onError: (error) => {
      return Promise.reject(error);
    },
  });
}
